import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ChevronRightRounded,
  LockOutlined,
  LoginRounded,
  MailOutlineRounded,
  PersonAddAltRounded,
} from "@mui/icons-material";
import AuthLayout, { authStyles as styles } from "../../components/customComponents/authLayout/AuthLayout";
import { brandConfig, loginConfig } from "../../auth/config";

/**
 * The default unauthenticated landing page ("/"): a router into whichever sign-in methods
 * auth/config.js's `loginConfig.methods` enables. Nothing on this page authenticates
 * anything by itself.
 *
 * The "sso" card is Kriha's real OAuth/SSO redirect flow, unchanged and reached at
 * `/login/sso` (pages/Login/Login.jsx).
 *
 * auth/ConfiguredLogin.jsx's LoginEntry only renders this component at all when more than one method
 * is enabled; a single-method white-label build (e.g. just ["password"]) skips this chooser
 * entirely and redirects straight to that method's page, purely through loginConfig - no route or
 * component change required.
 */
export default function LoginLanding() {
  const navigate = useNavigate();
  const accessToken = useSelector((state) => state.auth?.accessToken);

  // Already signed in (e.g. a bookmark, or a reload that restored the persisted store) - don't
  // make an authenticated user pick a sign-in method.
  if (accessToken) {
    return <Navigate to="/splash" replace />;
  }

  const options = [
    {
      id: "sso",
      icon: <LoginRounded />,
      title: "Single sign-on",
      subtitle: "Continue with your organization's Kriha SSO account.",
      to: "/login/sso",
    },
    {
      id: "password",
      icon: <LockOutlined />,
      title: "Email / phone and password",
      subtitle: "Sign in to your brand or manufacturer account.",
      to: "/login/password",
    },
    {
      id: "otp",
      icon: <MailOutlineRounded />,
      title: "Email and one-time code",
      subtitle: "We'll email you a 6-digit code - no password needed.",
      to: "/login/otp",
    },
  ];

  if (loginConfig.selfSignup) {
    options.push({
      id: "signup",
      icon: <PersonAddAltRounded />,
      title: "Create an account",
      subtitle: "New here? Set up your own account with your email address.",
      to: "/signup",
    });
  }

  const enabledOptions = options.filter(
    (option) => option.id === "signup" || loginConfig.methods.includes(option.id),
  );

  return (
    <AuthLayout
      wide
      heading="Welcome back"
      subheading={`Choose how you'd like to sign in to the ${brandConfig.workspaceName} workspace.`}
    >
      <div className={styles.optionList}>
        {enabledOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            className={styles.optionCard}
            onClick={() => navigate(option.to)}
          >
            <span className={styles.optionIcon}>{option.icon}</span>
            <span className={styles.optionCopy}>
              <span className={styles.optionTitle}>{option.title}</span>
              <span className={styles.optionSubtitle}>{option.subtitle}</span>
            </span>
            <ChevronRightRounded className={styles.optionChevron} />
          </button>
        ))}
      </div>
    </AuthLayout>
  );
}
